import { useEffect, useState } from 'react'

import { useLocation } from '@tanstack/react-router'

import { useContextStore } from '@/stores/context'

import { PlanPaywallDialog } from '../plan-paywall-dialog'

const DISPLAY_DELAY_MS = 5000

/**
 * Global PlanPaywallProvider component
 * Renders a non-dismissible PlanPaywallDialog when the account has no active
 * subscription, no free plan, and zero envelope balance.
 * Free-plan accounts are never hard-blocked here; publish-time 402 handles
 * them instead.
 * Suppressed on settings pages so the admin can complete subscription.
 * Should be rendered once at the root/app layout level.
 */
export function PlanPaywallProvider() {
  const location = useLocation()
  const accountContext = useContextStore((s) => s.userAccountContext)
  const [isReady, setIsReady] = useState(false)

  const shouldShow =
    accountContext &&
    !accountContext.hasActiveSubscription &&
    !accountContext.freePlanInformation &&
    accountContext.balanceInformation.envelopeBalance === 0

  useEffect(() => {
    if (!shouldShow) {
      setIsReady(false)
      return
    }

    const timer = setTimeout(() => setIsReady(true), DISPLAY_DELAY_MS)
    return () => clearTimeout(timer)
  }, [shouldShow])

  // Don't show on settings pages (so admin can subscribe or manage the
  // account), on the dashboard index route (the dashboard surfaces its own
  // non-blocking upgrade banner), or on affiliate pages (the affiliate
  // program is usable without any plan).
  if (
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/affiliate') ||
    location.pathname === '/'
  ) {
    return null
  }

  if (!shouldShow || !isReady) return null

  return <PlanPaywallDialog open={true} pagePath={location.pathname} />
}
